import React from "react";
import { Button } from "@material-tailwind/react";
import { useUser } from "../context/UserContext";
import { Edit, Trash2, Eye, Settings } from "lucide-react";

export const PrimaryButton = ({
  children,
  onClick,
  className,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-flamingo-500 hover:bg-flamingo-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
};

export const SecondaryButton = ({
  children,
  onClick,
  className,
  icon,
  type,
  left = false,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-flamingo-200 hover:bg-flamingo-300 text-flamingo-900 font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {left && icon}
      {children}
      {!left && icon}
    </Button>
  );
};

export const OutlineButton = ({
  children,
  onClick,
  className,
  icon,
  left = false,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      variant="outline"
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border border-flamingo-500 text-flamingo-500 hover:bg-flamingo-50 hover:text-flamingo-700 font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {left && icon}
      {children}
      {!left && icon}
    </Button>
  );
};

export const DarkNavButton = ({
  children,
  onClick,
  className,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-flamingo-700 hover:bg-flamingo-800 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
};

export const SmallButton = ({
  children,
  onClick,
  className,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-flamingo-500 hover:bg-flamingo-600 text-white font-bold py-1 px-3 rounded text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
};

export const DisabledButton = ({ children, className, disabled: explicitDisabled = false, ...rest }) => {
  const { isReadOnly } = useUser();
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      className={`w-40 border-none bg-flamingo-300 text-white font-bold py-2 px-4 rounded opacity-50 cursor-not-allowed ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
};

export const IconButton = ({
  children,
  onClick,
  icon,
  className,
  left = false,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-flamingo-500 hover:bg-flamingo-600 text-white font-bold py-2 px-4 rounded flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {left && icon}
      {children}
      {!left && icon}
    </Button>
  );
};

export const GradientButton = ({
  children,
  onClick,
  className,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      disabled={isDisabled}
      onClick={onClick}
      className={`w-40 border-none bg-gradient-to-r from-flamingo-400 to-flamingo-600 hover:from-flamingo-500 hover:to-flamingo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-flamingo-500 focus:ring-offset-2 ${className}`}
      {...rest}
    >
      {children}
    </Button>
  );
};

export const EditButton = ({
  onClick,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const { isReadOnly } = useUser();
  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      color="black"
      className="p-2"
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      <Edit size={17.5} />
    </Button>
  );
};

export const ViewButton = ({
  onClick,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const { isReadOnly } = useUser();
  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      color="black"
      className="p-2"
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      <Eye size={17.5} />
    </Button>
  );
};

export const SettingsButton = ({
  onClick,
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      color="black"
      className="p-2"
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      <Settings size={17.5} />
    </Button>
  );
};

export const CloseButton = ({
  onClick,
  className = "",
  disabled: explicitDisabled = false,
  ...rest
}) => {

  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      color="black"
      className={` ${className}`}
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="h-5 w-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </Button>
  );
};

export const DeleteButton = ({
  onClick,
  className = "",
  icon: Icon = Trash2, // Default to Trash2 if no icon is provided
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const { isReadOnly } = useUser();
  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      className={`px-2 py-2  text-red-500 hover:bg-red-600 border-none ${className}`}
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      <Icon size={17.5} />
    </Button>
  );
};

// A generic IconButton with customizable icon and default blue styling
export const SmallIconButton = ({
  onClick,
  className = "",
  icon: Icon, // icon component required
  disabled: explicitDisabled = false,
  ...rest
}) => {
  const isDisabled = explicitDisabled;

  return (
    <Button
      size="sm"
      variant="outline"
      className={`px-2 py-2 text-black-500 hover:bg-black-600 border-black-200 ${className}`}
      onClick={onClick}
      disabled={isDisabled}
      {...rest}
    >
      {Icon ? <Icon size={17.5} /> : null}
    </Button>
  );
};


